import { useCallback } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import type { TaskSubjectWrite } from "@storyboard/web-client";
import { recordTaskPinBoards, recordTasks, taskPinBoardWriteFor, taskWriteFor } from "@/models/task/taskWrites";
import type { DispatchTableRow } from "./dispatchTableRow";
import { openTaskOfTarget, type TaskAbout, type TaskFold, taskGrainFor, taskWritesFor } from "./tasks";

/** Who makes the tasks, and where: what every task write on the page is stamped with. */
export type TaskScope = { workspaceId: string; membershipId: string | undefined };

/**
 * The task writes the page makes: new tasks from rows or a History entry, and a task moved to another board.
 *
 * A bulk selection writes one batch. A message or event that already carries an open task gets that task moved to the
 * chosen board instead of a second task; a driver can carry many. A task made from rows is about each row's message
 * or event, or its driver, as the dispatcher chose; one from a History entry names the subject it is handed, whatever
 * the view's grain. A task closes only by being acknowledged, which the page's acknowledgement writer does.
 *
 * @param grain - The view's grain, which decides what a row's task is about.
 * @param scope - Who makes the tasks, and where.
 * @param fold - The workspace's open tasks, to find a row's task already in force.
 */
export function useTasks(
  grain: DispatchGrain,
  scope: TaskScope,
  fold: TaskFold,
): {
  makeTasks: (rows: readonly DispatchTableRow[], about: TaskAbout, viewId: string | null, note: string | null) => void;
  makeTaskFor: (subject: TaskSubjectWrite, viewId: string | null, note: string | null) => void;
  moveTask: (task: OpenTask, viewId: string) => void;
} {
  const { workspaceId, membershipId } = scope;
  const writer = useCallback(
    () => ({ workspaceId, actorWorkspaceMembershipId: membershipId ?? null }),
    [workspaceId, membershipId],
  );

  const makeTasks = useCallback(
    (rows: readonly DispatchTableRow[], about: TaskAbout, viewId: string | null, note: string | null) => {
      const { subjects, moves } = taskWritesFor(rows, taskGrainFor(grain, about), fold);
      recordTasks(
        writer(),
        subjects.map((subject) => taskWriteFor({ subjects: [subject], note, viewId })),
      );
      if (viewId !== null) {
        recordTaskPinBoards(
          writer(),
          moves.map(({ task }) => taskPinBoardWriteFor({ taskId: task.id, viewId })),
        );
      }
    },
    [grain, fold, writer],
  );

  const makeTaskFor = useCallback(
    (subject: TaskSubjectWrite, viewId: string | null, note: string | null) => {
      // A message or event that already carries an open task has that task moved; a driver gets another.
      const current = subject.subject === "membership" ? null : openTaskOfTarget(subject, fold);
      if (current === null) {
        recordTasks(writer(), [taskWriteFor({ subjects: [subject], note, viewId })]);
        return;
      }
      if (viewId !== null) {
        recordTaskPinBoards(writer(), [taskPinBoardWriteFor({ taskId: current.task.id, viewId })]);
      }
    },
    [fold, writer],
  );

  const moveTask = useCallback(
    (task: OpenTask, viewId: string) =>
      recordTaskPinBoards(writer(), [taskPinBoardWriteFor({ taskId: task.task.id, viewId })]),
    [writer],
  );

  return { makeTasks, makeTaskFor, moveTask };
}
